import { type FormEvent, useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PlusIcon, SearchIcon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { isPlatform, useSessionUser } from '@/shared/session/currentUser';
import { MIN_PASSWORD } from '@/shared/session/password';
import {
  CAPABILITY_LABELS,
  CAPABILITY_ORDER,
  PROFILE_HINTS,
  PROFILE_LABELS,
  PROFILE_ORDER,
  type UserProfile,
} from '@/shared/session/profiles';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

import { mailboxChips } from './personChanges';
import {
  Avatar,
  type Handout,
  LastSeen,
  MailboxChecks,
  PersonCard,
  StateBadges,
} from './PersonCard';
import type { AdminUser, InviteRead } from './types';
import {
  useAdminMailboxes,
  useAdminUsers,
  useCreateUser,
  useProfileCapabilities,
} from './useAdmin';

const timeFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

type HandoutMode = 'invite' | 'password';

const LIST = '/administracion/usuarios';

/**
 * The mailboxes the system reads: the ones registered in «Buzones» and the one the
 * connector reads from its environment. Contract v10.12: a person sees the mailboxes
 * assigned to them and nothing else, and only these can be assigned.
 */
function useKnownMailboxes(): string[] {
  const mailboxes = useAdminMailboxes();
  const known = new Set<string>();
  // A payroll-only mailbox is nobody's inbox (contract v10.53): the API refuses it here.
  for (const each of mailboxes.data?.items ?? []) {
    if (each.kind !== 'payroll') known.add(each.name);
  }
  for (const each of mailboxes.data?.environment_mailboxes ?? []) known.add(each);
  return [...known].sort();
}

/** Active first, then by name: the people somebody is looking for are at the top. */
function byName(a: AdminUser, b: AdminUser): number {
  if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
  return (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email, 'es');
}

function matches(user: AdminUser, query: string): boolean {
  const words = query
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const text = [user.full_name ?? '', user.email, ...(user.mailboxes ?? [])]
    .join(' ')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
  return words.every((word) => text.includes(word));
}

/**
 * Who enters, and what each person sees and does (contract v10.58, ADR 0055 of the back).
 *
 * A list and a card, the way an address book is read: on the left everybody in one line
 * each (name, mailboxes, profile, the exceptions, when they last entered), on the right the
 * card of the person chosen (`/administracion/usuarios/:userId`, so a card is a link). On a
 * phone, one or the other. With nobody chosen, the right side says what each profile sees.
 *
 * Before v10.58 this was one wide table where every checkbox saved on its own; for an
 * administrator of the shop the alta column stayed empty beside it and pushed the «⋯» of
 * each row out of view. Now the card holds a draft and says what will change before
 * saving (`PersonCard`); switching to another person with changes pending asks first.
 *
 * Who gets in is Ignira's call, for now (contract v10.16): «Dar de alta» is only there for
 * a superadministrator. An administrator of the shop does not see the superadministrators
 * at all; the back leaves them out of the list and the filter here covers an older one.
 */
export function UsersTab() {
  const me = useSessionUser();
  const platform = isPlatform(me);
  const users = useAdminUsers();
  const known = useKnownMailboxes();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [shown, setShown] = useState<Handout | null>(null);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState(false);
  const [leaving, setLeaving] = useState<string | null>(null);
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), []);

  const rows = (users.data?.items ?? [])
    .filter((user) => platform || !user.is_superadmin)
    .sort(byName);
  const listed = query.trim() ? rows.filter((user) => matches(user, query)) : rows;
  const selected = userId ? rows.find((user) => user.id === userId) : undefined;

  /** Away from a card with changes pending, only after asking. */
  function go(path: string) {
    if (dirty && path !== `${LIST}/${userId ?? ''}`) setLeaving(path);
    else void navigate(path);
  }

  return (
    <div className="flex flex-col gap-4">
      {shown?.kind === 'password' ? (
        <Callout tone="ok" title="Contraseña puesta. Se enseña una sola vez.">
          <p>
            <strong>{shown.email}</strong>: <code className="select-all">{shown.password}</code>
          </p>
          <p className="mt-1 text-[13px]">
            Dásela en persona. En su primer acceso tendrá que elegir la suya.
          </p>
        </Callout>
      ) : null}
      {shown?.kind === 'invite' ? <InviteCallout invite={shown.invite} /> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(18rem,23rem)_minmax(0,1fr)]">
        <Card
          title="Personas"
          aside={
            platform ? (
              <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                <PlusIcon className="size-4" /> Dar de alta
              </Button>
            ) : rows.length ? (
              <span className="text-ink-3 text-[13px]">{rows.length}</span>
            ) : null
          }
          padded={false}
          className={cn('min-w-0 lg:sticky lg:top-4', userId ? 'hidden lg:flex' : null)}
        >
          {users.isPending ? <Spinner variant="fill" label="Cargando usuarios" /> : null}
          {users.isError ? (
            <ErrorMessage error={users.error} className="m-4">
              No se pudo cargar la lista de personas
            </ErrorMessage>
          ) : null}
          {users.data ? (
            <>
              {rows.length > 6 ? (
                <div className="border-line-2 border-b p-3">
                  <label className="relative block">
                    <span className="sr-only">Buscar persona</span>
                    <SearchIcon className="text-ink-4 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                    <input
                      type="search"
                      className="field pl-9"
                      placeholder="Nombre, correo o buzón"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                    />
                  </label>
                </div>
              ) : null}
              <ul aria-label="Personas" className="divide-line-2 flex flex-col divide-y">
                {listed.map((user) => (
                  <li key={user.id}>
                    <PersonRow
                      user={user}
                      current={user.id === userId}
                      onOpen={() => go(`${LIST}/${user.id}`)}
                    />
                  </li>
                ))}
              </ul>
              {listed.length === 0 ? (
                <p className="text-ink-3 p-4 text-[13.5px]">Nadie con «{query}».</p>
              ) : null}
            </>
          ) : null}
        </Card>

        {selected ? (
          <PersonCard
            key={selected.id}
            user={selected}
            known={known}
            onHandout={setShown}
            onDirtyChange={onDirtyChange}
            backTo={LIST}
          />
        ) : userId && users.data ? (
          <Card title="No está en la lista">
            <p className="text-ink-2 text-[14px]">
              Esa persona no existe o no la puedes gestionar.{' '}
              <Link to={LIST} className="link">
                Volver a Personas
              </Link>
            </p>
          </Card>
        ) : (
          <ProfilesOverview className="hidden lg:flex" />
        )}
      </div>

      {adding ? (
        <Dialog title="Dar de alta a alguien" onClose={() => setAdding(false)}>
          <NewUserForm
            known={known}
            onCreated={(handout, created) => {
              setShown(handout);
              setAdding(false);
              void navigate(`${LIST}/${created.id}`);
            }}
          />
        </Dialog>
      ) : null}

      {leaving ? (
        <Dialog
          title="¿Descartar los cambios sin guardar?"
          size="sm"
          onClose={() => setLeaving(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setLeaving(null)} data-autofocus>
                Seguir editando
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const path = leaving;
                  setLeaving(null);
                  setDirty(false);
                  void navigate(path);
                }}
              >
                Descartarlos
              </Button>
            </>
          }
        >
          <p className="text-ink-2 text-[14.5px] leading-relaxed">
            La ficha que estás editando tiene cambios que no se han guardado. Si sigues, se pierden.
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}

/** One person in one line: who, what they read, what they see, and only the exceptions. */
function PersonRow({
  user,
  current,
  onOpen,
}: {
  user: AdminUser;
  current: boolean;
  onOpen: () => void;
}) {
  const chips = mailboxChips(user.mailboxes ?? []);
  return (
    <Link
      to={`${LIST}/${user.id}`}
      aria-current={current ? 'page' : undefined}
      aria-label={user.full_name ? `${user.full_name}, ${user.email}` : user.email}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onOpen();
      }}
      className={cn(
        'flex gap-3 px-4 py-3 text-left',
        current ? 'bg-accent-soft/50' : 'hover:bg-paper-2',
        user.is_active ? null : 'text-ink-3',
      )}
    >
      <Avatar user={user} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-ink truncate text-[14px] font-medium">
            {user.full_name ?? user.email}
          </span>
          {user.is_admin && !user.is_superadmin ? (
            <span className="text-ink-3 shrink-0 text-[12px]">Administra</span>
          ) : null}
        </span>
        {user.full_name ? (
          <span className="text-ink-3 truncate text-[12.5px] leading-4">{user.email}</span>
        ) : null}
        <span className="flex flex-wrap items-center gap-1">
          {user.is_superadmin ? null : chips.length ? (
            chips.map((chip) => (
              <Badge key={chip.title} title={chip.title}>
                {chip.label}
              </Badge>
            ))
          ) : (
            <Badge tone="warn">sin buzones</Badge>
          )}
          {user.is_superadmin ? null : (
            <span className="text-ink-3 text-[12px]">· {PROFILE_LABELS[user.profile]}</span>
          )}
        </span>
        <StateBadges user={user} />
        {user.last_seen_at !== undefined ? (
          <span className="text-ink-4 text-[12px]">
            <LastSeen value={user.last_seen_at} />
          </span>
        ) : null}
      </span>
    </Link>
  );
}

/** With nobody chosen: what each profile sees, from the API's own table (contract v10.58). */
function ProfilesOverview({ className }: { className: string }) {
  const capabilities = useProfileCapabilities();
  return (
    <Card
      title="Qué ve cada perfil"
      description="Elige a alguien de la lista para ver y cambiar sus buzones, su perfil y su acceso."
      className={className}
      padded={false}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-ink-3 text-left text-[12px] tracking-wide uppercase">
              <th className="px-4 py-2 font-medium">
                <span className="sr-only">Qué</span>
              </th>
              {PROFILE_ORDER.map((profile) => (
                <th key={profile} scope="col" className="px-3 py-2 text-center font-medium">
                  {PROFILE_LABELS[profile]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-line-2 divide-y">
            {CAPABILITY_ORDER.map((capability) => (
              <tr key={capability}>
                <th scope="row" className="text-ink-2 px-4 py-2 text-left font-normal">
                  {CAPABILITY_LABELS[capability]}
                </th>
                {PROFILE_ORDER.map((profile) => {
                  const has = capabilities[profile].includes(capability);
                  return (
                    <td key={profile} className="px-3 py-2 text-center">
                      {has ? (
                        <span className="text-ok font-semibold" aria-label="Sí">
                          ✓
                        </span>
                      ) : (
                        <span className="text-ink-4" aria-label="No">
                          —
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-ink-3 border-line-2 border-t px-4 py-3 text-[12.5px] leading-snug">
        El correo no depende del perfil: cada persona ve los buzones que tiene asignados.
      </p>
    </Card>
  );
}

/**
 * The link, shown once: the API does not keep it anywhere the panel could read it back
 * from. Copied with a button because a 60-character token is not something to select by
 * hand on a phone; the text stays selectable for when the clipboard is not available.
 */
function InviteCallout({ invite }: { invite: InviteRead }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Callout
      tone="ok"
      title="Enlace de invitación. Se enseña una sola vez."
      action={
        <Button type="button" size="sm" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copiado' : 'Copiar enlace'}
        </Button>
      }
    >
      <p>
        Para <strong>{invite.email}</strong>. Vale hasta el{' '}
        {formatDate(timeFormat, invite.expires_at)} y una sola vez.
      </p>
      <p className="mt-1">
        <code className="block break-all select-all">{invite.link}</code>
      </p>
      <p className="mt-1 text-[13px]">
        Pásaselo a esa persona, no a un grupo: quien lo abra elige la contraseña de la cuenta.
        {invite.sent_by_email ? ' También ha salido por correo.' : ''}
      </p>
    </Callout>
  );
}

function NewUserForm({
  known,
  onCreated,
}: {
  known: string[];
  onCreated: (shown: Handout, created: AdminUser) => void;
}) {
  const platform = isPlatform(useSessionUser());
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mailboxes, setMailboxes] = useState<string[]>([]);
  /** The most restricted, like the API's default: whoever gives it widens it on purpose. */
  const [profile, setProfile] = useState<UserProfile>('comercial');
  const [mode, setMode] = useState<HandoutMode>('invite');
  const [password, setPassword] = useState('');
  const create = useCreateUser();

  const passwordMissing = mode === 'password' && password.length < MIN_PASSWORD;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const typed = password;
    const base = {
      email,
      full_name: fullName || null,
      is_superadmin: isSuperadmin,
      is_admin: isAdmin,
      mailboxes,
      profile,
    };
    const payload = mode === 'invite' ? { ...base, invite: true } : { ...base, password: typed };
    create.mutate(payload, {
      onSuccess: (user) => {
        if (user.invite) onCreated({ kind: 'invite', invite: user.invite }, user);
        else onCreated({ kind: 'password', email: user.email, password: typed }, user);
      },
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Dar de alta a alguien">
      <p className="text-ink-3 text-[13px]">
        La persona entra con el enlace o la contraseña que le des y elige la suya.
      </p>
      <Input
        label="Correo"
        type="email"
        autoComplete="off"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
      />
      <Input
        label="Nombre"
        optional
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
      />
      {!isSuperadmin ? (
        <div className="flex flex-col gap-1">
          <span className="text-ink-2 text-[13px] font-medium">Buzones que lee</span>
          <MailboxChecks label="Buzones" known={known} value={mailboxes} onChange={setMailboxes} />
          <p className="text-ink-3 text-[12.5px] leading-snug">
            Verá esos buzones enteros y ninguno más. Sin buzón, la bandeja le sale vacía.
          </p>
        </div>
      ) : null}
      {!isSuperadmin ? (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-ink-2 mb-1 text-[13px] font-medium">Perfil</legend>
          {PROFILE_ORDER.map((each) => (
            <label key={each} className="text-ink-2 flex items-start gap-2 text-[14px]">
              <input
                type="radio"
                name="new-user-profile"
                className="accent-accent mt-0.5 size-4"
                checked={profile === each}
                onChange={() => setProfile(each)}
              />
              <span>
                {PROFILE_LABELS[each]}
                <span className="text-ink-3 block text-[12.5px] leading-snug">
                  {PROFILE_HINTS[each]}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {platform ? (
        <label className="text-ink-2 flex items-start gap-2 text-[14px]">
          <input
            type="checkbox"
            className="accent-accent mt-0.5 size-4"
            checked={isSuperadmin}
            onChange={(event) => {
              setIsSuperadmin(event.target.checked);
              if (event.target.checked) setIsAdmin(false);
            }}
          />
          <span>
            Superadministrador
            <span className="text-ink-3 block text-[12.5px] leading-snug">
              Ignira: todos los buzones, Estadísticas, Accesos y Datos. Solo para quien mantiene la
              plataforma.
            </span>
          </span>
        </label>
      ) : null}
      {platform && !isSuperadmin ? (
        <label className="text-ink-2 flex items-start gap-2 text-[14px]">
          <input
            type="checkbox"
            className="accent-accent mt-0.5 size-4"
            checked={isAdmin}
            onChange={(event) => setIsAdmin(event.target.checked)}
          />
          <span>
            Administra el negocio
            <span className="text-ink-3 block text-[12.5px] leading-snug">
              Usuarios, buzones, destinos y activación, sea cual sea su perfil. No amplía el correo
              que ve: solo sus buzones. Víctor y Ernesto.
            </span>
          </span>
        </label>
      ) : null}
      <div className="flex flex-col gap-1">
        <span className="text-ink-2 text-[13px] font-medium">Su primera contraseña</span>
        <Segmented<HandoutMode>
          label="Cómo recibe su primera contraseña"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'invite', label: 'Con un enlace' },
            { value: 'password', label: 'La escribo yo' },
          ]}
        />
        <p className="text-ink-3 text-[12.5px] leading-snug">
          {mode === 'invite'
            ? 'Te damos un enlace que vale 24 horas y una sola vez. Se lo pasas y elige su contraseña.'
            : 'Escribes una contraseña provisional, se enseña una vez y la persona la cambia al entrar.'}
        </p>
      </div>
      {mode === 'password' ? (
        <Input
          label="Primera contraseña"
          type="text"
          autoComplete="off"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          minLength={MIN_PASSWORD}
          hint={`Mínimo ${MIN_PASSWORD} caracteres.`}
          required
        />
      ) : null}
      <Button type="submit" disabled={create.isPending || passwordMissing}>
        {create.isPending
          ? 'Dando de alta…'
          : mode === 'invite'
            ? 'Dar de alta y generar enlace'
            : 'Dar de alta'}
      </Button>
      {create.isError ? (
        <p role="alert" className="text-danger text-[13.5px]">
          {errorMessage(create.error)}
        </p>
      ) : null}
    </form>
  );
}
